// telephonyWizardConfig.js
// Config-driven step registry for the telephony onboarding wizard. Both entry
// modes ("number" / "receptionist") reuse the SAME step components — only the
// ORDER differs. buildTelephonySteps() computes the ordered array from the entry
// mode plus the upsell answer, so the wizard shell stays dumb (it just walks an
// array). Mirrors the Create-Customer wizard's step-config + sidebar approach.

import { SelectNumberStep } from "./Steps/SelectNumberStep";
import { ConfirmPurchaseStep } from "./Steps/ConfirmPurchaseStep";
import { UpsellReceptionistStep } from "./Steps/UpsellReceptionistStep";
import { ReceptionistConfigStep } from "./Steps/ReceptionistConfigStep";
import { ProvisionStep } from "./Steps/ProvisionStep";
import { LinkNumberStep } from "./Steps/LinkNumberStep";
import { SummaryStep } from "./Steps/SummaryStep";

// The full catalogue of steps, keyed by id. buildTelephonySteps picks from here.
export const TELEPHONY_STEPS = {
  selectNumber: {
    id: "selectNumber",
    title: "Choose a Number",
    component: SelectNumberStep,
  },
  confirmPurchase: {
    id: "confirmPurchase",
    title: "Confirm & Buy",
    component: ConfirmPurchaseStep,
  },
  upsellReceptionist: {
    id: "upsellReceptionist",
    title: "Add Receptionist?",
    component: UpsellReceptionistStep,
  },
  receptionistConfig: {
    id: "receptionistConfig",
    title: "Answering Prompt",
    component: ReceptionistConfigStep,
  },
  provision: {
    id: "provision",
    title: "Create Assistant",
    component: ProvisionStep,
  },
  linkNumber: {
    id: "linkNumber",
    title: "Link Number",
    component: LinkNumberStep,
  },
  summary: {
    id: "summary",
    title: "Finish",
    component: SummaryStep,
  },
};

/**
 * Compute the ordered list of steps for the wizard.
 *
 * entryMode "number":
 *   SelectNumber → ConfirmPurchase → UpsellReceptionist
 *     → (upsell yes)  ReceptionistConfig → Provision → LinkNumber → Summary
 *     → (upsell no)   Summary
 *
 * entryMode "receptionist":
 *   ReceptionistConfig → Provision
 *     → (prompt "your receptionist needs a number")
 *       SelectNumber → ConfirmPurchase → LinkNumber → Summary
 *
 * @param {"number"|"receptionist"} entryMode
 * @param {boolean|null} wantsReceptionist upsell answer (number mode only)
 * @returns {Array<{id:string,title:string,component:Function}>}
 */
export function buildTelephonySteps(entryMode, wantsReceptionist) {
  const S = TELEPHONY_STEPS;

  if (entryMode === "receptionist") {
    return [
      S.receptionistConfig,
      S.provision,
      S.selectNumber,
      S.confirmPurchase,
      S.linkNumber,
      S.summary,
    ];
  }

  // Default: entryMode === "number"
  const steps = [S.selectNumber, S.confirmPurchase, S.upsellReceptionist];

  if (wantsReceptionist === true) {
    steps.push(S.receptionistConfig, S.provision, S.linkNumber);
  }

  steps.push(S.summary);
  return steps;
}
